import { OrderResponse } from '@afterpaytouch/portal-api'
import React, { createContext, Dispatch, FC, ReactNode, SetStateAction, useCallback, useContext, useMemo, useState } from 'react'
import { Money } from '@afterpay/types'

// @ts-ignore: OPERATION BLEED STOPPER
const noop = (): void => null

export enum Section75DisputeModalSteps {
  ClaimEligibility,
  ChooseItems,
  NotEligible,
  DisputeDetails,
  DisputeAmount,
  OtherDocs,
  Review,
}

export interface Section75DisputeModalContextResult {
  success: boolean
  error: boolean
  message?: string
  title?: string
}

export interface SelectedItem {
  key: number
  name: string
  amount: Money
  itemAmount?: Money
  quantity?: number
  disputeAmount?: Money
  useCustomAmount?: boolean
  amountReason?: string
  reason?: string
  description: string
  fileKeys: string[]
}
export interface Section75DisputeModalContextProps {
  step: Section75DisputeModalSteps
  setStep: (step: Section75DisputeModalSteps) => void
  resetState: () => void
  order: OrderResponse
  setOrder: (order: OrderResponse) => void
  closeModal: () => void
  result: Section75DisputeModalContextResult
  setResult: (result: Section75DisputeModalContextResult) => void
  isLoading: boolean
  setIsLoading: (loading: boolean) => void
  reason: string
  setReason: (reason: string) => void
  expectedDeliveryDate: string | null
  setExpectedDeliveryDate: (date: string | null) => void
  useCustomAmount: boolean
  setUseCustomAmount: (useCustomAmount: boolean) => void
  customAmount: number
  setCustomAmount: (customAmount: number) => void
  amountReason: string
  setAmountReason: (amountReason: string) => void
  files: File[]
  setFiles: (files: File[]) => void
  refreshDisputeInfo: () => void
  selectedItems: SelectedItem[]
  setSelectedItems: Dispatch<SetStateAction<SelectedItem[]>>
  currentItemIndex: number | null
  setCurrentItemIndex: (index: number | null) => void
  proofOfPurchaseUploaded: boolean
  setProofOfPurchaseUploaded: (uploaded: boolean) => void
  termsAndConditionsUploaded: boolean
  setTermsAndConditionsUploaded: (uploaded: boolean) => void
}

export const Section75DisputeModalContextDefaultValue: Section75DisputeModalContextProps = {
  // @ts-ignore: OPERATION BLEED STOPPER
  step: null,
  setStep: noop,
  resetState: noop,
  // @ts-ignore: OPERATION BLEED STOPPER
  order: null,
  setOrder: noop,
  closeModal: noop,
  result: {
    success: false,
    error: false,
  },
  setResult: noop,
  isLoading: false,
  setIsLoading: noop,
  reason: '',
  setReason: noop,
  expectedDeliveryDate: null,
  setExpectedDeliveryDate: noop,
  useCustomAmount: false,
  setUseCustomAmount: noop,
  customAmount: 0,
  setCustomAmount: noop,
  amountReason: '',
  setAmountReason: noop,
  files: [],
  setFiles: noop,
  refreshDisputeInfo: noop,
  selectedItems: [],
  setSelectedItems: noop,
  currentItemIndex: null,
  setCurrentItemIndex: noop,
  proofOfPurchaseUploaded: false,
  setProofOfPurchaseUploaded: noop,
  termsAndConditionsUploaded: false,
  setTermsAndConditionsUploaded: noop,
}

export const Section75DisputeModalContext = createContext<Section75DisputeModalContextProps>(Section75DisputeModalContextDefaultValue)

export const useSection75DisputeModalContext = (): Section75DisputeModalContextProps => {
  return { ...useContext(Section75DisputeModalContext) }
}

export interface Section75DisputeModalProviderProps {
  order?: OrderResponse
  refreshDisputeInfo: () => void
  closeModal: () => void
  children?: ReactNode
}

export const Section75DisputeModalProvider: FC<Section75DisputeModalProviderProps> = ({ children, order: defaultOrder, closeModal, refreshDisputeInfo }) => {
  const initialResult = useMemo(() => ({ success: false, error: false }), [])
  const [step, setStep] = useState<Section75DisputeModalSteps>(Section75DisputeModalSteps.ClaimEligibility)
  const [order, setOrder] = useState(defaultOrder ?? null)
  const [result, setResult] = useState<Section75DisputeModalContextResult>(initialResult)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [expectedDeliveryDate, setExpectedDeliveryDate] = useState<string | null>(null)
  const [useCustomAmount, setUseCustomAmount] = useState<boolean>(false)
  const [customAmount, setCustomAmount] = useState<number>(0)
  const [files, setFiles] = useState<File[]>([])
  const [reason, setReason] = useState<string>('')
  const [amountReason, setAmountReason] = useState<string>('')
  const [selectedItems, setSelectedItems] = useState<SelectedItem[]>([])
  const [currentItemIndex, setCurrentItemIndex] = useState<number | null>(null)
  const [proofOfPurchaseUploaded, setProofOfPurchaseUploaded] = useState<boolean>(false)
  const [termsAndConditionsUploaded, setTermsAndConditionsUploaded] = useState<boolean>(false)

  const resetState = useCallback(() => {
    setStep(Section75DisputeModalSteps.ClaimEligibility)
    setResult(initialResult)
    setReason('')
    setAmountReason('')
    setExpectedDeliveryDate(null)
    setUseCustomAmount(false)
    setCustomAmount(0)
    setIsLoading(false)
    setFiles([])
    setSelectedItems([])
    setCurrentItemIndex(null)
    setProofOfPurchaseUploaded(false)
    setTermsAndConditionsUploaded(false)
  }, [initialResult])

  const value = {
    step,
    setStep,
    order,
    setOrder,
    closeModal,
    result,
    setResult,
    resetState,
    isLoading,
    setIsLoading,
    reason,
    setReason,
    expectedDeliveryDate,
    setExpectedDeliveryDate,
    useCustomAmount,
    setUseCustomAmount,
    customAmount,
    setCustomAmount,
    amountReason,
    setAmountReason,
    files,
    setFiles,
    refreshDisputeInfo,
    selectedItems,
    setSelectedItems,
    currentItemIndex,
    setCurrentItemIndex,
    proofOfPurchaseUploaded,
    setProofOfPurchaseUploaded,
    termsAndConditionsUploaded,
    setTermsAndConditionsUploaded,
  }
  // @ts-ignore: OPERATION BLEED STOPPER
  return <Section75DisputeModalContext.Provider value={value}>{children}</Section75DisputeModalContext.Provider>
}
